import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListingCard from '../../components/ListingCard';
import {
  ListingCardListing,
  ListingCardAddress,
} from '../../components/ListingCard/types';
import {
  buildPropertyChips,
  budgetDisplay,
  sellerEstimateInfoText,
  sellerPriceDisplay,
} from '../../components/ListingCard/cardUtils';
import {
  formatStreetLine,
  formatAddressLine,
} from '../../components/utils/addressFormatting';
import { listingShareUrl } from '../../components/utils/listingSlug';
import { useOpenListing } from '../../components/utils/useOpenListing';
import { apiRequest } from '../../components/utils/api';
import { getBuildingListings } from '../../components/utils/buildingListings';
import { fetchConversationIdMap } from '../../components/utils/conversationBadges';
import { shareUrlWithFallback } from '../../utils/shareUtils';
import { CloseIcon } from '../../components/common/CloseIcon';
import {
  TipBuyerButton,
  TipBuyerMenu,
  TipPreviewModal,
  useTipBuyer,
} from '../../components/common/TipBuyer';
import shareIcon from '../../components/common/icons/share.svg';
import overflowIcon from '../../components/common/icons/overflow.svg';
import chatBubbleIcon from '../../components/common/icons/chat-bubble.svg';
import viewMapIcon from '../../components/common/icons/view-map.svg';
import BuildingGlyph from '../../components/common/icons/BuildingGlyph';
import PhotoCarousel from '../../components/common/PhotoCarousel/PhotoCarousel';
import ListingCardRow from '../../components/ListingCard/ListingCardRow';
import DemandBuyerCard from '../../components/AppContent/NewLandingPage/DemandBuyerCard';
import {
  AreaDemandCard,
  AreaInterest,
  getAreaInterestsForBuilding,
  getMyAreaInterests,
} from '../../components/AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import { areasLabel } from '../../components/AppContent/NewLandingPage/CouldBuyFlow/areaInterestCard';
import BuildingLocationMap from '../../components/BuildingModal/BuildingLocationMap';
import MultiImageModal from '../../components/ImageModal/MultiImageModal';
import ZoomIcon from '../../components/BuildingModal/Comments/ZoomIcon';
import EstimateInfoTooltip from '../../components/common/EstimateInfoTooltip/EstimateInfoTooltip';
import useIsDesktop from '../../components/BuildingModal/BuildingView/useIsDesktop';
import {
  BuildingWithConvertedAddresses,
  LatLng,
} from '../../components/utils/types';
import sellerHouseGraphic from '../../components/common/icons/house.png';
import buyerCoinGraphic from '../../components/common/icons/coin.png';
import { toListingHeroImage } from './imageSources';
import './ListingPage.scss';

const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

/**
 * Public listing payload from `GET /listings/:id`. A superset of
 * `ListingCardListing`: the card address type omits `coordinates`, but the BE
 * returns the full building address (with coordinates), which drives the map.
 * `building_id` is optional so a listing with no building still resolves.
 */
interface ListingDetail extends Omit<ListingCardListing, 'address'> {
  address: ListingCardAddress & { coordinates?: { lat: number; lng: number } };
  /** Representative centre of an area interest, returned by the public API. */
  map_center?: LatLng;
  /** An area interest's raw picks, so the page can label them in the viewer's
   *  own language rather than render the server's Finnish-only line. */
  display_areas?: AreaInterest['display_areas'];
  anchor_address?: AreaInterest['anchor_address'];
}

/**
 * One of a building's own listings, from `GET /buildings/:id/listings`. It
 * carries `buildingId` (camelCase) rather than the details endpoint's
 * `building_id`, so it is mapped separately in `feedEventToCardListing`.
 */
/** Response of `GET /area-interests/:id/listings`. Each side pages separately,
 *  so either flag can keep the rows asking for more. */
interface AreaListingsResponse {
  listings?: ListingDetail[];
  has_more_sellers?: boolean;
  has_more_buyers?: boolean;
}

// Matches the backend's per-side cap; the offset advances by a page at a time.
const AREA_PAGE_SIZE = 10;

interface FeedListing extends Omit<ListingCardListing, 'address'> {
  buildingId?: string;
  feed_event_type?: string;
  address: ListingCardAddress;
}

interface CouldBuyEligibility {
  listingId: string;
  buildingId: string | undefined;
  blocked: boolean;
  /** The viewer owns this listing: gates the buyer prompt's Could sell and Suggest. */
  own: boolean;
}

/**
 * Reshapes a `GET /listings/:id` payload onto the card's `ListingCardListing`
 * shape. `updated_at` falls back to '' (the card's "New" badge guards empty
 * values), so a BE that omits it simply renders no badge.
 */
const toCardListing = (d: ListingDetail): ListingCardListing => ({
  _id: d._id,
  building_id: d.building_id,
  could_buy: d.could_buy,
  could_sell: d.could_sell,
  property_description: d.property_description,
  property_size: d.property_size,
  property_rooms: d.property_rooms,
  property_floor: d.property_floor,
  property_has_elevator: d.property_has_elevator,
  property_has_balcony: d.property_has_balcony,
  property_renovation_status: d.property_renovation_status,
  property_asking_price: d.property_asking_price,
  estimate_status: d.estimate_status,
  estimated_price: d.estimated_price,
  estimated_price_range: d.estimated_price_range,
  estimated_price_currency: d.estimated_price_currency,
  address: d.address,
  images: d.images,
  area_interest_id: d.area_interest_id,
  criteria: d.criteria,
  updated_at: d.updated_at ?? '',
  // Carries the building feed's ownership flag through to the card, which uses
  // it for the teal --own border and to suppress Suggest on your own interest.
  // Absent on the public /listings/:id payload, which has no viewer to compare.
  isUserOwnEvent: d.isUserOwnEvent,
  verified: d.verified,
});

/**
 * Relabels an area interest's `address.street` in the viewer's language.
 * The server bakes that line itself for the share card, whose page is
 * `lang="fi"`, so an unanchored circle arrives as the Finnish "500 m säde" and
 * reaches an English reader untranslated (JAA-370). Every other consumer of
 * `address` on this page then follows from the one substitution. A building
 * listing has a real street and is returned untouched.
 */
const withLocalizedAreaLabel = (
  d: ListingDetail,
  t: (key: string, opts?: Record<string, unknown>) => string,
): ListingDetail => {
  if (!d.area_interest_id || !d.display_areas) return d;
  const anchor = d.anchor_address ? formatStreetLine(d.anchor_address) : '';
  return {
    ...d,
    address: {
      ...d.address,
      street: areasLabel(
        { display_areas: d.display_areas },
        t,
        anchor || undefined,
      ),
    },
  };
};

/**
 * Reshapes a building-feed OFF_MARKET_WILLINGNESS event onto the card shape,
 * reading `buildingId` (feed camelCase) into `building_id`.
 */
const feedEventToCardListing = (e: FeedListing): ListingCardListing => ({
  ...toCardListing(e as unknown as ListingDetail),
  building_id: e.buildingId,
});

/**
 * Props supplied when the view is opened as a shell modal (via ModalOutlet). All
 * optional so the component still works if rendered directly from a route.
 */
interface ListingPageProps {
  /** Listing id from the `/listing/:id` route; falls back to route params. */
  listingId?: string;
  /** Auth state from the shell; falls back to an internal check-login fetch. */
  isLoggedIn?: boolean;
  /**
   * True once the shell's /check-login-status has answered. `isLoggedIn` reads
   * as signed-out until then, so viewer-context fetches wait on this rather
   * than firing once anonymously and again when auth settles.
   */
  hasCheckedLogin?: boolean;
  /** Shell close handler; falls back to history.goBack / explore. */
  onClose?: () => void;
  /**
   * Opens the shell's login modal; falls back to routing to explore. Takes the
   * visibility flag because the shell hands over its `useState` setter directly
   * — calling it with no argument sets the flag to undefined, which reads as
   * closed and silently swallows the prompt.
   */
  setLoginModalVisible?: (visible: boolean) => void;
  /**
   * Opens the building view for the given id; falls back to a URL push. `seed`
   * is a minimal building (id + centroid + address) the shell can render
   * immediately while the full document loads; null when this listing has no
   * coordinates.
   */
  onViewBuilding?: (
    buildingId: string,
    seed: BuildingWithConvertedAddresses | null,
  ) => void;
  /** Returns an area interest to the Explore map, centred on the interest. */
  onViewMap?: (center?: LatLng) => void;
}

/**
 * Signed-out-safe single-listing view (JAA-306). Fetches the public listing and
 * renders the big card plus building context — mirroring the building page's
 * design (white sheet, dividered sections, location map, Sellers/Buyers
 * carousels). Rendered as a shell modal (reusing the app header) via ModalOutlet,
 * driven by the `/listing/:id` route; also opens from a building-view card tap.
 */
const ListingPage: React.FC<ListingPageProps> = ({
  listingId,
  isLoggedIn: isLoggedInProp,
  hasCheckedLogin,
  onClose,
  setLoginModalVisible,
  onViewBuilding,
  onViewMap,
}) => {
  const { id: paramId } = useParams<{ id: string; slug?: string }>();
  const id = listingId ?? paramId;
  const { t } = useTranslation();
  const history = useHistory();
  const isDesktop = useIsDesktop();
  const openListing = useOpenListing();
  const [listing, setListing] = useState<ListingDetail | null>(null);
  const [otherListings, setOtherListings] = useState<ListingCardListing[]>([]);
  // Area buyers are fetched separately because they are a separate collection:
  // the building feed only knows willingness rows anchored to this building,
  // while an area interest covers the building by geometry alone (JAA-347).
  const [areaBuyers, setAreaBuyers] = useState<AreaDemandCard[]>([]);
  const [couldBuyEligibility, setCouldBuyEligibility] =
    useState<CouldBuyEligibility | null>(null);
  // Paging state for the area rows. Building listings still arrive in one shot,
  // so these stay at their defaults there.
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedOffset, setRelatedOffset] = useState(0);
  const [relatedHasMore, setRelatedHasMore] = useState(false);
  const [relatedLoadingMore, setRelatedLoadingMore] = useState(false);
  // Which listing the related rows currently belong to, as `listingId::areaId`.
  // A continuation request compares against this before writing, so a response
  // that arrives after the page moved on is dropped instead of appended. The
  // listing id has to be part of it: two listings can share one area interest,
  // and an area-only key would let the old request pass while it still filters
  // on the previous listing's id.
  const relatedTargetRef = useRef<string | undefined>(undefined);
  // The in-flight guard has to be synchronous. Both rows arm their own
  // reach-end watcher, so on a page where each has content the two can call
  // loadMoreRelated in the same tick — before React has applied the state flag
  // — and fetch the same offset twice, each incrementing it, skipping a page.
  // relatedLoadingMore stays as the flag the row renders its skeleton from.
  const relatedLoadingMoreRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedInState, setIsLoggedIn] = useState(false);
  const isLoggedIn = isLoggedInProp ?? isLoggedInState;
  // JAA-355: the viewer's existing conversation with this seller, so the prompt
  // reads "Open chat" instead of offering a first message. Resolved separately
  // because the listing itself is served without a session.
  const [existingConversationId, setExistingConversationId] = useState<
    string | null
  >(null);
  // Whether isLoggedIn can be trusted yet: it reads as signed-out while either
  // source's check is in flight, so a viewer-context fetch that ignores this
  // runs anonymously and then has to run again once auth lands. A caller that
  // passes isLoggedIn without hasCheckedLogin is handing us an already-settled
  // value, so don't wait on a signal it will never send.
  const [authCheckedState, setAuthChecked] = useState(false);
  const authChecked =
    hasCheckedLogin ?? (isLoggedInProp !== undefined || authCheckedState);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Full-size photo opened from the hero gallery; null while the lightbox is closed.
  // Opens the browsable gallery over the hero carousel. A single-photo modal
  // forced a close/reopen per picture, which is unusable for a 20-photo listing.
  const [galleryOpen, setGalleryOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // The prompt's inline position, and whether it has scrolled past the topbar —
  // at which point the same buttons ride a docked bar at the bottom, as they do
  // on the building page.
  const promptRef = useRef<HTMLDivElement>(null);
  const [ctaDocked, setCtaDocked] = useState(false);
  const addressRef = useRef<HTMLHeadingElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Auth state powers the buyer cards' Suggest/Share (same as the building view).
  // The shell passes it in as a prop; only fetch it ourselves when rendered
  // standalone (prop undefined).
  useEffect(() => {
    if (isLoggedInProp !== undefined) return;
    apiRequest('/check-login-status', { method: 'GET' })
      .then((res: { isLoggedIn?: boolean }) => setIsLoggedIn(!!res?.isLoggedIn))
      .catch(() => setIsLoggedIn(false))
      // Settled either way: a failed check means signed-out, which is a usable
      // answer. Leaving this false would stall the related/ownership fetch.
      .finally(() => setAuthChecked(true));
  }, [isLoggedInProp]);

  // JAA-355: ask for the viewer's conversation with this listing's seller once
  // the session is known. Buyer listings and signed-out viewers have none.
  useEffect(() => {
    setExistingConversationId(null);
    if (
      !authChecked ||
      !isLoggedIn ||
      !listing ||
      listing._id !== id ||
      !listing.could_sell
    ) {
      return;
    }
    let cancelled = false;
    const listingId = listing._id;
    fetchConversationIdMap([listingId])
      .then((map) => {
        if (!cancelled) setExistingConversationId(map[listingId] ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [authChecked, isLoggedIn, listing, id]);

  // Sticky-topbar scroll morph (mirrors the building view): a 0→1 progress driven
  // by how far the address has scrolled up to the bar drives the bar's fading
  // divider/shadow and the address collapse, written straight to the DOM at
  // 60fps. Capture-phase so it fires whichever ancestor actually scrolls.
  useEffect(() => {
    const BAR = 64;
    // Facts-first layout: with stats present the address renders compact (24px),
    // so the bar-title scale is 18/24; without stats it stays the big 32px h1
    // (18/32). Must match the __address / __address--compact SCSS transforms.
    const statsShown = !!(listing?.property_size || listing?.property_rooms);
    const addressFontSize = statsShown ? 24 : 32;
    const update = () => {
      const root = rootRef.current;
      // Docking is measured first and on its own: the address morph below needs
      // the address + sentinel, which only the mobile layout renders, and
      // bailing out on them left the desktop layout with a bar that never rose.
      const promptEl = promptRef.current;
      if (root && promptEl) {
        const rootTop = root.getBoundingClientRect().top;
        const promptBottom = promptEl.getBoundingClientRect().bottom;
        setCtaDocked((prev) =>
          prev
            ? promptBottom < rootTop + BAR + 48
            : promptBottom < rootTop + BAR,
        );
      }
      const addr = addressRef.current;
      const sentinel = sentinelRef.current;
      if (!root || !addr || !sentinel) return;
      // Progress from the sentinel (the address's original in-flow position); the
      // address itself is sticky so can't measure its own scroll.
      const dist =
        sentinel.getBoundingClientRect().top - root.getBoundingClientRect().top;
      const p = Math.min(1, Math.max(0, (BAR - dist) / BAR));
      root.style.setProperty('--topbar-p', `${p}`);
      // Px the scaled (→18px) address must travel right to land centred in the
      // bar — measured, since the address width varies (building view logic).
      // Centred on the content column width (capped), not the full scroll pane.
      const SMALL = 18 / addressFontSize;
      const GUTTER = 20;
      const colW = root.clientWidth;
      const cx = (colW - addr.offsetWidth * SMALL) / 2 - GUTTER;
      root.style.setProperty('--addr-cx', `${Math.max(0, cx)}px`);
      // Single discrete swap at the midpoint (matches the building view): the
      // View-building pill → icon, and Share → the "..." menu.
      setScrolled(p >= 0.5);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [listing]);

  // The buyers this building's area interests represent, on their own request
  // and their own effect. They are public and anonymized, so unlike the building
  // feed they answer the same for every viewer: waiting for the login check
  // would delay them for a signed-out visitor and refetch them for no reason the
  // moment a session resolves. Building listings only — an area interest carries
  // a building_id too, but that is merely the building its buyer started from,
  // and asking it for area buyers filed a whole district's demand under one
  // interest's neighbours.
  useEffect(() => {
    const buildingId = listing?.building_id;
    if (listing?._id !== id || !buildingId || listing?.area_interest_id) {
      setAreaBuyers([]);
      return undefined;
    }
    let cancelled = false;
    getAreaInterestsForBuilding(buildingId, {
      // Only a fallback: the backend answers from the building's own centroid
      // when it has one, so this page and the building view cannot disagree
      // about a building the database knows.
      coords: listing?.address?.coordinates,
    }).then((rows) => {
      // null is a dropped request; an empty list means no area covers this
      // building, which is the ordinary case and is authoritative.
      if (!cancelled && rows) setAreaBuyers(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [listing, id]);

  // Every setState is gated on `cancelled` so a response that lands after the id
  // changed (or after unmount) can't write a listing the page no longer shows,
  // nor clear the spinner the newer request just raised.
  useEffect(() => {
    let cancelled = false;
    const fetchListing = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const data = (await apiRequest(`/listings/${id}`)) as ListingDetail;
        if (!cancelled) setListing(withLocalizedAreaLabel(data, t));
      } catch (err) {
        console.error('Error fetching listing:', err);
        if (!cancelled) setError(t('listingView.notFound'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchListing();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  // "Other listings" depends on the resolved building. Authentication is also
  // a dependency because the building feed only marks the viewer's own events
  // for authenticated requests; logging in must invalidate the anonymous
  // could-buy eligibility result.
  useEffect(() => {
    // "Here" means this listing's own place: a building listing shares its
    // building, an area interest its areas. Keying both off building_id put one
    // street's listings under a heading that promised a district, because an
    // interest's building_id is only the anchor the buyer started from.
    const resolvedListingId = listing?._id;
    const areaInterestId = listing?.area_interest_id;
    const buildingId = listing?.building_id;
    // Each target pages on its own: inheriting the previous listing's offset and
    // has-more let a new area load page two before its first page had landed.
    relatedTargetRef.current = `${id}::${areaInterestId ?? ''}`;
    setRelatedOffset(0);
    setRelatedHasMore(false);
    relatedLoadingMoreRef.current = false;
    setRelatedLoadingMore(false);
    // Drop the previous target's cards up front. A building target resolves
    // without a loading state, so leaving them would show the last listing's
    // neighbours under this one's heading until the fetch lands — or for good,
    // if it fails.
    setOtherListings([]);
    setCouldBuyEligibility(null);
    // The listing in state is still the previous page's until its own fetch
    // lands, and it cannot answer for this id: fetching on it would file another
    // listing's neighbours under this one and resolve the could-buy prompt from
    // the wrong building feed.
    if (!resolvedListingId || resolvedListingId !== id) {
      setRelatedLoading(false);
      return undefined;
    }
    setRelatedLoading(!!areaInterestId);
    // An area interest still asks whether it is the viewer's own, even without an anchor building.
    if (!buildingId && !areaInterestId) {
      // No building feed to check the contact against, so nothing can block it
      // and the prompt resolves as eligible without a request.
      setCouldBuyEligibility({
        listingId: resolvedListingId,
        buildingId,
        blocked: false,
        own: false,
      });
    }
    if (!areaInterestId && !buildingId) return undefined;
    // Wait for auth to settle before asking. The building feed only marks the
    // viewer's own rows on an authenticated request, so firing while the login
    // check is still in flight resolves ownership as unknown and costs a second
    // pass to correct.
    if (!authChecked) return undefined;
    let cancelled = false;
    // The buyers a building's own area interests represent, fetched on their own
    // request rather than alongside the building feed. Sharing that request's
    // fate is what the ticket is about: the feed failing, or merely being slow,
    // must not turn seven buyers into none, and the Could buy button below must
    // not wait on this one to resolve.
    const fetchOthers = async () => {
      // Eligibility is only ever blocked on proof, never on silence: a failed
      // request, a non-array body, an area-interest listing the feed does not
      // cover, or a row missing from the page all settle as eligible. Leaving it
      // unresolved would disable Could buy for good, and the backend still
      // rejects a seller contacting their own listing.
      const resolved: CouldBuyEligibility = {
        listingId: resolvedListingId,
        buildingId,
        blocked: false,
        own: false,
      };
      try {
        if (areaInterestId) {
          // No public payload names an area interest's owner, so a signed-in viewer checks their own list; each request settles alone so one failing cannot discard the other.
          const [res, mine] = await Promise.all([
            (
              apiRequest(
                `/area-interests/${areaInterestId}/listings`,
              ) as Promise<AreaListingsResponse>
            ).catch((err) => {
              console.error('Error fetching other listings:', err);
              return null;
            }),
            isLoggedIn ? getMyAreaInterests().catch(() => []) : [],
          ]);
          resolved.own = mine.some(
            (interest) => interest.area_interest_id === areaInterestId,
          );
          const rows = Array.isArray(res?.listings) ? res.listings : [];
          if (!cancelled) {
            // The interest's own listing stands inside its own footprint, so the
            // endpoint returns it; without this it renders in its own row.
            setOtherListings(
              rows.filter((r) => r._id !== id).map(toCardListing),
            );
            setRelatedOffset(AREA_PAGE_SIZE);
            setRelatedHasMore(
              !!res?.has_more_sellers || !!res?.has_more_buyers,
            );
          }
          return;
        }
        // The building's own listings. The public listing endpoint has no viewer
        // context, while these rows mark the ones belonging to the signed-in
        // user, which is what resolves the could-buy prompt.
        const rows = (await getBuildingListings(buildingId)) as
          | FeedListing[]
          | null;
        if (!Array.isArray(rows)) return;
        resolved.blocked = rows.some(
          (c) =>
            !!c.isUserOwnEvent &&
            (c._id === resolvedListingId || !!c.could_sell),
        );
        // Own row only: already selling in the building must not lock Suggest, which tips with that listing.
        resolved.own = rows.some(
          (c) => c._id === resolvedListingId && !!c.isUserOwnEvent,
        );
        const mapped = rows
          .filter((c) => c._id !== resolvedListingId)
          .map(feedEventToCardListing);
        if (!cancelled) {
          setOtherListings(mapped);
        }
      } catch (err) {
        console.error('Error fetching other listings:', err);
      } finally {
        // Also settles on the error path, so a failed request cannot leave the
        // rows showing skeletons or the Could buy button disabled forever.
        if (!cancelled) {
          setCouldBuyEligibility(resolved);
          setRelatedLoading(false);
        }
      }
    };

    fetchOthers();
    return () => {
      cancelled = true;
    };
    // isLoggedIn re-runs this: ownership (the feed flag, the viewer's own area interests) is session-dependent.
  }, [listing, id, authChecked, isLoggedIn]);

  // Signed-out login prompt for the Suggest controls: prefer the shell's login
  // modal; else route to explore where the login modal lives. Declared before
  // the early returns so the tip-buyer hook below can reference it.
  const promptLogin = () => {
    if (setLoginModalVisible) {
      setLoginModalVisible(true);
      return;
    }
    history.push('/');
  };

  // Tip-buyer "Suggest" for the buyer prompt (same flow as BuyerCard's footer).
  // Hooks must precede the loading/error returns; the empty id during loading
  // is safe — the hook only fetches when the button is clicked.
  const tipBuyer = useTipBuyer({
    buyWillingnessId: listing?._id ?? '',
    isLoggedIn,
  });

  /**
   * Append the next page of area listings. Guarded on its own in-flight flag so
   * a fast scroll at the right edge cannot queue two requests for one page, and
   * on the initial load so no page is fetched before the first one lands.
   *
   * Every state write re-checks the interest the request was issued for. The
   * page can be navigated to another listing mid-flight, and without the check
   * a late response would append its rows to the new listing and leave the
   * in-flight flag set, which would stop the new row ever paging.
   */
  const loadMoreRelated = React.useCallback(async () => {
    const areaInterestId = listing?.area_interest_id;
    if (
      !areaInterestId ||
      relatedLoading ||
      relatedLoadingMoreRef.current ||
      !relatedHasMore
    ) {
      return;
    }
    const target = `${id}::${areaInterestId}`;
    const isCurrent = () => relatedTargetRef.current === target;
    relatedLoadingMoreRef.current = true;
    setRelatedLoadingMore(true);
    try {
      const res = (await apiRequest(
        `/area-interests/${areaInterestId}/listings?offset=${relatedOffset}`,
      )) as AreaListingsResponse;
      if (!isCurrent()) return;
      const rows = Array.isArray(res?.listings) ? res.listings : [];
      setOtherListings((prev) => {
        const seen = new Set(prev.map((l) => l._id));
        return [
          ...prev,
          ...rows
            .map(toCardListing)
            .filter((l) => l._id !== id && !seen.has(l._id)),
        ];
      });
      setRelatedOffset((o) => o + AREA_PAGE_SIZE);
      setRelatedHasMore(!!res?.has_more_sellers || !!res?.has_more_buyers);
    } catch (err) {
      console.error('Error loading more area listings:', err);
      // Stop asking rather than retry on every scroll tick.
      if (isCurrent()) setRelatedHasMore(false);
    } finally {
      // Left set when superseded; the target reset owns clearing it, so a late
      // response cannot re-open paging for a listing that is no longer shown.
      if (isCurrent()) {
        relatedLoadingMoreRef.current = false;
        setRelatedLoadingMore(false);
      }
    }
  }, [
    listing?.area_interest_id,
    id,
    relatedOffset,
    relatedHasMore,
    relatedLoading,
  ]);

  if (loading) {
    return (
      <div className="listing-page">
        <div className="listing-page__loading">
          <div className="listing-page__spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="listing-page">
        <div className="listing-page__error">
          <p>{error || t('listingView.notFound')}</p>
          <a href="/" className="listing-page__error-link">
            {t('listingView.explore')}
          </a>
        </div>
      </div>
    );
  }

  const cardListing = toCardListing(listing);
  const coordinates = listing.address.coordinates;
  const areaInterestCenter = listing.map_center;
  const buildingId = listing.building_id;

  // Hero (Airbnb-style, not a card): a prominent gallery + the facts laid out
  // directly on the page.
  // District line under the street heading (building-page style: "00120
  // Helsinki"), so the street isn't repeated from the h1.
  const districtLine = [listing.address.postcode, listing.address.city]
    .filter(Boolean)
    .join(' ');
  const isBuyer = !!listing.could_buy && !listing.could_sell;
  const isAreaBuyInterest = isBuyer && !!listing.area_interest_id;
  const heroImages = (listing.images ?? []).map(toListingHeroImage);
  // MultiImageModal's Image shape, built the same way the listing card does it.
  const galleryImages = heroImages.map((im) => ({
    url: im.url,
    thumbnailUrl: im.thumbnailUrl,
    metadata: { dimensions: { width: 0, height: 0 }, size_kb: 0 },
  }));
  const heroChips = buildPropertyChips(cardListing, t, {
    includeElevator: true,
  });
  const hasStats = !!listing.property_size || !!listing.property_rooms;
  // A buyer's price line is the budget they set, not an asking price they hold.
  // Sellers share the card's priority: asking price, completed Jaakko estimate,
  // pending estimate, then the unset label.
  const sellerPrice = sellerPriceDisplay(cardListing, t);
  const priceLabel = isBuyer ? budgetDisplay(cardListing) : sellerPrice.text;

  const renderPriceLabel = () => {
    if (
      isBuyer ||
      sellerPrice.kind === 'asking' ||
      sellerPrice.kind === 'unset'
    ) {
      return priceLabel;
    }
    return (
      <span className="listing-page__price-wrap">
        <span>{sellerPrice.text}</span>
        <EstimateInfoTooltip<HTMLButtonElement>
          ariaLabel={t(
            'listingCard.estimateInfoAria',
            'About this Jaakko estimate',
          )}
          content={sellerEstimateInfoText(sellerPrice.kind, t)}
        >
          {(triggerProps) => (
            <button
              {...triggerProps}
              type="button"
              className="listing-page__price-avatar-trigger"
            >
              <img
                src={JAAKKO_AVATAR}
                alt=""
                aria-hidden="true"
                className="listing-page__price-avatar"
              />
            </button>
          )}
        </EstimateInfoTooltip>
      </span>
    );
  };

  // Close: prefer the shell's handler (pops the entry that opened the view);
  // else returns to where the user came from, falling back to explore for a
  // cold/shared load.
  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  // Shares a listing via the backend share URL (OG preview + redirect to the
  // canonical route) — used by the hero and the seller cards.
  const shareListing = (l: { _id: string }) =>
    shareUrlWithFallback({ url: listingShareUrl(l._id), t });

  const handleShare = () => shareListing(listing);

  // Minimal building for BuildingLocationMap, which centres on
  // centroid || addresses[0].coordinates and drops the house marker there.
  const mapBuilding = coordinates
    ? ({
        id: buildingId ?? '',
        centroid: coordinates,
        addresses: [{ ...listing.address, coordinates }],
        geometry: { type: 'Polygon', coordinates: [] },
        properties: { name: '', addresses: [] },
      } as unknown as BuildingWithConvertedAddresses)
    : null;

  // Other listings in this building, split into sell listings and buy interests
  // (an off-market willingness is one or the other).
  const otherSellers = otherListings.filter((l) => l.could_sell);
  const otherBuyers = otherListings.filter((l) => l.could_buy && !l.could_sell);
  // Area interests render through the same buyer card the building view uses,
  // so a buyer reads the same either side of the navigation.
  const areaBuyerCards = areaBuyers.map((card) => (
    <DemandBuyerCard
      key={card.area_interest_id}
      card={card}
      isLoggedIn={isLoggedIn}
      setLoginModalVisible={promptLogin}
      onOpen={openListing}
    />
  ));

  // View building: prefer the shell handler (opens the building view directly);
  // else fall back to a URL push (works on a fresh AppContent mount). The seed
  // carries the listing's own id + coordinates so the shell can paint the
  // building view at once instead of waiting on its fetch.
  const handleViewBuilding = () => {
    if (!buildingId) return;
    if (onViewBuilding) {
      onViewBuilding(buildingId, mapBuilding);
      return;
    }
    history.push(`/?building-id=${buildingId}`);
  };

  // Area interests are not tied to their anchor building. Return to Explore's
  // map centred on the interest instead of opening that building as if it were
  // the interest's location.
  const handleViewMap = () => {
    if (onViewMap) {
      onViewMap(areaInterestCenter);
      return;
    }
    handleClose();
  };

  // A seller listing can open a chat without a building seed, so the prompt
  // renders for every visitor: a signed-out one gets the draft chat and is
  // asked to log in at Send (JAA-325), which is the whole point of showing it
  // to them. The buyer prompt renders for every buy listing too: Suggest only
  // needs the interest's own id, and an area interest carries no building_id
  // at all, so gating the whole prompt on one left those listings with no
  // action on the page.
  const showCouldBuyPrompt = !isBuyer;
  const showCouldSellPrompt = isBuyer;

  // Whether the building feed has answered for THIS listing: a stale answer
  // from the previously viewed listing would speak for the wrong building.
  const couldBuyEligibilityResolved =
    listing._id === id &&
    couldBuyEligibility?.listingId === listing._id &&
    couldBuyEligibility.buildingId === buildingId;
  // The button stays visible but goes dead when the contact cannot succeed:
  // the feed says this is the viewer's own listing, or that they already sell
  // in the building; the backend rejects both. Nothing to resolve while
  // signed out (the feed marks no rows as yours, and Send asks for login
  // anyway), but a signed-in viewer waits for the answer rather than being
  // offered a chat that turns out to be with themselves. Pending auth reads as
  // signed-out, so it waits too.
  const couldBuyDisabled =
    !authChecked ||
    (isLoggedIn &&
      (!couldBuyEligibilityResolved || couldBuyEligibility.blocked));
  // Same wait-then-block rule for the buyer prompt, blocked only on the viewer's own buy listing.
  const couldSellDisabled =
    !authChecked ||
    (isLoggedIn && (!couldBuyEligibilityResolved || couldBuyEligibility.own));

  // Opens Messages on this seller's existing conversation or an ephemeral draft
  // chat. The first message creates the conversation; clicking alone writes
  // nothing server-side. Signed-out visitors get the same draft chat, and are
  // asked to authenticate when they press Send (JAA-325).
  const handleCouldBuy = () => {
    window.dispatchEvent(
      new CustomEvent('jaakko:open-draft-chat', {
        detail: {
          sellWillingnessId: listing._id,
          title: formatAddressLine(listing.address),
        },
      }),
    );
  };

  // Opens Messages on the conversation the viewer already has with this
  // seller; same event the listing cards dispatch for their "Open chat" pill.
  const handleOpenChat = () => {
    window.dispatchEvent(
      new CustomEvent('jaakko:open-conversation', {
        detail: { conversationId: existingConversationId },
      }),
    );
  };

  // Opens the AMP "Sell here" flow (AddMyPlaceFlow under NewLandingPage) seeded
  // with this listing's building — same custom-event pattern as handleCouldBuy,
  // since the modal stack has no prop path down to the flow. Postcode rides
  // along because the AMP seed needs it (unlike the could-buy seed).
  const handleCouldSell = () => {
    // An area interest's building is the buyer's own starting point, not a
    // place the viewer can sell, so that case opens the flow unseeded and lets
    // them type their own address. A building-backed buy listing still seeds
    // the building the buyer is asking about.
    const seed =
      buildingId && !isAreaBuyInterest
        ? {
            buildingId,
            coordinates: listing.address.coordinates ?? null,
            address: {
              street: listing.address.street ?? '',
              houseNumber: listing.address.houseNumber ?? '',
              city: listing.address.city ?? '',
              country: listing.address.country ?? '',
              postcode: listing.address.postcode ?? '',
            },
          }
        : {};
    window.dispatchEvent(
      new CustomEvent('jaakko:open-could-sell', { detail: seed }),
    );
  };

  /**
   * A titled row of big cards, matching the building view — including its split
   * by breakpoint: desktop uses EdgeFadeScroller, whose drag + vertical-wheel →
   * horizontal scrolling is the only way to move the row on a pointer device
   * (the scrollbar is hidden by design), while mobile keeps the snap carousel's
   * swipe + dots.
   */
  const renderListingSection = (
    title: string,
    items: ListingCardListing[],
    buyersRow = false,
    extraCards: React.ReactNode[] = [],
  ) => {
    if (items.length === 0 && extraCards.length === 0 && !relatedLoading) {
      return null;
    }
    const cards = items.map((other) => {
      const isSeller = !(other.could_buy && !other.could_sell);
      return (
        <ListingCard
          key={other._id}
          size="big"
          type={isSeller ? 'seller' : 'buyer'}
          listing={other}
          isLoggedIn={isLoggedIn}
          setLoginModalVisible={promptLogin}
          onOpen={() => openListing(other)}
          onShare={isSeller ? () => shareListing(other) : undefined}
        />
      );
    });
    return (
      <ListingCardRow
        title={title}
        cards={[...cards, ...extraCards]}
        isDesktop={isDesktop}
        loading={relatedLoading}
        hasMore={relatedHasMore}
        loadingMore={relatedLoadingMore}
        onLoadMore={loadMoreRelated}
        className="listing-page__section"
        titleClassName="listing-page__section-title"
        trackClassName={`listing-page__card-row${
          buyersRow ? ' listing-page__card-row--buyers' : ''
        }`}
      />
    );
  };

  // Seller/Buyer type badge on the hero placeholder's top-left corner, so a
  // visitor immediately sees which kind of listing this is. Buyer heroes always
  // render the placeholder (images forced empty below), so the badge always
  // shows; seller heroes only badge when there are no photos — exactly when the
  // house placeholder is visible. Text-only: the placeholder already carries
  // the house/coin graphic.
  const showTypeBadge = isBuyer || heroImages.length === 0;

  /** The listing's photo gallery (Airbnb-style hero), shared by both layouts. */
  const renderGallery = () => (
    <div className="listing-page__gallery">
      <PhotoCarousel
        block="listing-card"
        images={isBuyer ? [] : heroImages}
        // Page-wide hero: use the active full-size URL. JAA-399 will bound
        // legacy originals that predate display optimization.
        imageSize="display"
        onImageClick={() => setGalleryOpen(true)}
        trailingControls={
          !isBuyer && heroImages.length > 0 ? (
            <button
              type="button"
              className="listing-card__view-all"
              aria-label={t('listingCard.viewAllPhotos', 'View all photos')}
              onClick={(e) => {
                e.stopPropagation();
                setGalleryOpen(true);
              }}
            >
              <ZoomIcon color="#fff" size={18} />
            </button>
          ) : null
        }
        activeAlt={formatStreetLine(listing.address)}
        labels={{
          prev: t('listingCard.prevPhoto', 'Previous photo'),
          next: t('listingCard.nextPhoto', 'Next photo'),
          goTo: (i: number) => t('listingCard.goToPhoto', { index: i + 1 }),
        }}
        badge={
          showTypeBadge ? (
            <span
              className={`listing-page__type-badge${
                isBuyer ? ' listing-page__type-badge--buyer' : ''
              }`}
            >
              {isBuyer
                ? t('listingView.buyerBadge', 'Buyer')
                : t('listingView.sellerBadge', 'Seller')}
            </span>
          ) : null
        }
        emptyContent={
          <div className="listing-card__visual-empty" aria-hidden="true">
            <img
              className="listing-card__empty-graphic"
              src={isBuyer ? buyerCoinGraphic : sellerHouseGraphic}
              alt=""
            />
          </div>
        }
      />
      {galleryOpen && (
        <MultiImageModal
          images={galleryImages}
          closeImageModal={() => setGalleryOpen(false)}
        />
      )}
    </div>
  );

  // Whether the main-facts header has anything to show: stats, a price line
  // (always rendered for sellers, even "No set price"), a buyer's budget, or
  // chips. The budget counts on its own — a buyer whose only criterion is a
  // price range has no stats and no chips, and was rendering nothing at all.
  const hasMainFacts =
    hasStats || !isBuyer || !!priceLabel || heroChips.length > 0;

  /**
   * Main facts, shown facts-first ABOVE the address + gallery: stats (the big
   * heading), price, chips. When there are no stats the address stays the big
   * h1 instead and this block (price + chips) renders below the caption.
   */
  const renderMainFacts = () =>
    hasMainFacts ? (
      <div className="listing-page__facts-header">
        {hasStats && (
          <h1 className="listing-page__stats">
            {!!listing.property_size && <span>{listing.property_size}m²</span>}
            {!!listing.property_size && !!listing.property_rooms && (
              <span className="listing-page__stats-dot" aria-hidden="true" />
            )}
            {!!listing.property_rooms && (
              <span>
                {listing.property_rooms} {t('listingCard.rooms')}
              </span>
            )}
          </h1>
        )}

        {(!isBuyer || !!priceLabel) && (
          <p
            className={`listing-page__price${
              !isBuyer && sellerPrice.kind !== 'asking'
                ? ` listing-page__price--${sellerPrice.kind}`
                : ''
            }`}
          >
            {renderPriceLabel()}
          </p>
        )}

        {heroChips.length > 0 && (
          <div className="listing-page__chips">
            {heroChips.map((chip) => (
              <span className="listing-page__chip" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>
    ) : null;

  /** Description text — the only fact that stays BELOW the gallery. */
  const renderDescription = () =>
    listing.property_description ? (
      <p className="listing-page__description">
        {listing.property_description}
      </p>
    ) : null;

  /**
   * Prompt card under the description (building-view __prompt style). Seller
   * listings: "Interested?" copy + the full-width mint could-buy button (the
   * building "Buy here" pill with the card's Could-buy label + chat icon).
   * Buyer listings: "Do you own a home…" copy + a two-pill row like the
   * building view's prompt — yellow Could-sell (AMP flow) left, mint Suggest
   * (tip-buyer flow, as on BuyerCard) right.
   */
  /**
   * The prompt's buttons on their own, so the inline card and the docked bar
   * render the same actions. `inDock` says which copy this is: the tip menu
   * anchors to whichever one the reader can currently see, since the other has
   * scrolled out of the viewport and would open the menu off-screen.
   */
  const renderPromptActions = (inDock: boolean) => {
    const isLiveCopy = inDock === ctaDocked;
    if (showCouldBuyPrompt && existingConversationId) {
      return (
        <button
          type="button"
          className="listing-page__could-buy-btn"
          onClick={handleOpenChat}
        >
          <img
            className="listing-page__could-buy-icon"
            src={chatBubbleIcon}
            alt=""
            aria-hidden="true"
          />
          {t('listingCard.openChat', 'Open chat')}
        </button>
      );
    }
    if (showCouldBuyPrompt) {
      return (
        <button
          type="button"
          className="listing-page__could-buy-btn"
          onClick={handleCouldBuy}
          disabled={couldBuyDisabled}
        >
          <img
            className="listing-page__could-buy-icon"
            src={chatBubbleIcon}
            alt=""
            aria-hidden="true"
          />
          {t('listingCard.messageSeller', 'Send message')}
        </button>
      );
    }
    return (
      <div className="listing-page__prompt-actions">
        <button
          type="button"
          className="listing-page__could-sell-btn"
          onClick={handleCouldSell}
          disabled={couldSellDisabled}
        >
          {t('listingCard.couldSell', 'Could sell')}
        </button>
        <div className="listing-page__suggest-wrap">
          <TipBuyerButton
            onClick={tipBuyer.handleButtonClick}
            buttonRef={isLiveCopy ? tipBuyer.buttonRef : undefined}
            className="tip-buyer-button--listing-page"
            disabled={couldSellDisabled}
          />
          {isLiveCopy && (
            <TipBuyerMenu
              isOpen={tipBuyer.showMenu}
              menuRef={tipBuyer.menuRef}
              buttonRef={tipBuyer.buttonRef}
              isLoggedIn={isLoggedIn}
              isLoadingListings={tipBuyer.isLoadingListings}
              dailyLimitReached={tipBuyer.dailyLimitReached}
              userSellListings={tipBuyer.userSellListings}
              tippedListings={tipBuyer.tippedListings}
              onSelectListing={tipBuyer.handleSelectListing}
              onLoginClick={() => {
                tipBuyer.closeMenu();
                promptLogin();
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderPrompt = () => {
    if (showCouldBuyPrompt && existingConversationId) {
      return (
        <div className="listing-page__prompt" ref={promptRef}>
          <p className="listing-page__prompt-text">
            {t(
              'listingView.openChatPrompt',
              'You already have a chat with the seller.',
            )}
          </p>
          {renderPromptActions(false)}
        </div>
      );
    }
    if (showCouldBuyPrompt) {
      return (
        <div className="listing-page__prompt" ref={promptRef}>
          <p className="listing-page__prompt-text">
            {t(
              'listingView.interestedPrompt',
              'Interested? Send a message to the seller.',
            )}
          </p>
          {renderPromptActions(false)}
        </div>
      );
    }
    if (showCouldSellPrompt) {
      return (
        <div className="listing-page__prompt" ref={promptRef}>
          <p className="listing-page__prompt-text">
            {t(
              'listingView.buyerPrompt',
              'Do you own a home this buyer is looking for? Let them know.',
            )}
          </p>
          {renderPromptActions(false)}
          <TipPreviewModal
            isOpen={tipBuyer.showPreviewModal}
            selectedListing={tipBuyer.selectedListing}
            isSendingTip={tipBuyer.isSendingTip}
            tipSentSuccess={tipBuyer.tipSentSuccess}
            onConfirm={tipBuyer.handleConfirmTip}
            onClose={tipBuyer.closePreviewModal}
          />
        </div>
      );
    }
    return null;
  };

  /** The docked copy of the prompt's buttons; empty when there is no prompt. */
  const renderDockedCta = () => {
    if (!showCouldBuyPrompt && !showCouldSellPrompt) return null;
    return (
      <div
        className={`listing-page__cta${
          ctaDocked ? ' listing-page__cta--docked' : ''
        }`}
        role="presentation"
      >
        {renderPromptActions(true)}
      </div>
    );
  };

  /** Other sellers/buyers carousels + the location map, shared by both layouts. */
  const renderSections = () => (
    <>
      {mapBuilding && (
        <section className="listing-page__section">
          <h2 className="listing-page__section-title">
            {t('building.map.title', 'Map')}
          </h2>
          <p className="listing-page__section-caption">
            {formatAddressLine(listing.address)}
          </p>
          <div className="listing-page__map">
            <BuildingLocationMap selectedBuilding={mapBuilding} />
          </div>
        </section>
      )}

      {/* "Other" only fits the section matching the viewed listing's own side:
          on a sell listing the sellers are "other", the buyers are not. */}
      {renderListingSection(
        isBuyer
          ? t('listingView.sellersHere', 'Sellers here')
          : t('listingView.otherSellersHere', 'Other sellers here'),
        otherSellers,
      )}
      {renderListingSection(
        isBuyer
          ? t('listingView.otherBuyersHere', 'Other buyers here')
          : t('listingView.buyersHere', 'Buyers here'),
        otherBuyers,
        true,
        areaBuyerCards,
      )}
    </>
  );

  // Desktop (≥1024px): a static, centred content column mirroring the building
  // page's desktop layout — a text-action row, a facts-first header (stats as
  // the big heading, price, chips, then the compact address), an inset hero
  // banner, the description, then the shared sections. No scroll-morph topbar.
  if (isDesktop) {
    return (
      <div className="listing-page listing-page--desktop" ref={rootRef}>
        <div className="listing-page__page">
          <div className="listing-page__content">
            <div className="listing-page__page-actions">
              {(isAreaBuyInterest || buildingId) && (
                <button
                  type="button"
                  className="listing-page__text-action"
                  onClick={
                    isAreaBuyInterest ? handleViewMap : handleViewBuilding
                  }
                >
                  {isAreaBuyInterest ? (
                    <img
                      className="listing-page__text-action-icon"
                      src={viewMapIcon}
                      alt=""
                      aria-hidden="true"
                    />
                  ) : (
                    <BuildingGlyph className="listing-page__text-action-icon" />
                  )}
                  {isAreaBuyInterest
                    ? t('listingView.viewOnMap')
                    : t('listingView.viewBuilding')}
                </button>
              )}
              <div className="listing-page__page-actions-right">
                <button
                  type="button"
                  className="listing-page__text-action"
                  onClick={handleShare}
                >
                  <img
                    className="listing-page__text-action-icon"
                    src={shareIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  {t('listingCard.share', 'Share')}
                </button>
                <button
                  type="button"
                  className="listing-page__text-action"
                  onClick={handleClose}
                >
                  <CloseIcon size={18} color="#29363d" />
                  {t('common.close')}
                </button>
              </div>
            </div>

            <div className="listing-page__page-header">
              {hasStats ? (
                <>
                  {renderMainFacts()}
                  <h2 className="listing-page__address listing-page__address--compact">
                    {formatStreetLine(listing.address)}
                  </h2>
                  <p className="listing-page__caption">{districtLine}</p>
                </>
              ) : (
                <>
                  {/* No stats → the address keeps the big-h1 slot; price/chips
                      follow the caption. */}
                  <h1 className="listing-page__address">
                    {formatStreetLine(listing.address)}
                  </h1>
                  <p className="listing-page__caption">{districtLine}</p>
                  {renderMainFacts()}
                </>
              )}
            </div>

            <div className="listing-page__hero-banner">{renderGallery()}</div>

            {renderDescription()}

            {renderPrompt()}

            {renderSections()}
          </div>
        </div>
        {renderDockedCta()}
      </div>
    );
  }

  return (
    <div className="listing-page" ref={rootRef}>
      <div className="listing-page__col">
        {/* z3 bar fill, below the sticky address (z4) but behind the buttons
            (z5) — the layering that lets the address morph INTO the bar. */}
        <div className="listing-page__topbar-bg" aria-hidden="true" />
        <div
          className={`listing-page__topbar${
            scrolled ? ' listing-page__topbar--scrolled' : ''
          }`}
        >
          {(isAreaBuyInterest || buildingId) && (
            <button
              type="button"
              className="listing-page__view-building"
              aria-label={
                isAreaBuyInterest
                  ? t('listingView.viewOnMap')
                  : t('listingView.viewBuilding')
              }
              onClick={isAreaBuyInterest ? handleViewMap : handleViewBuilding}
            >
              {isAreaBuyInterest ? (
                <img
                  className="listing-page__view-building-icon"
                  src={viewMapIcon}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <BuildingGlyph className="listing-page__view-building-icon" />
              )}
              <span className="listing-page__view-building-text">
                {isAreaBuyInterest
                  ? t('listingView.viewOnMap')
                  : t('listingView.viewBuilding')}
              </span>
            </button>
          )}

          {/* Share + Close show as a pair at the top and collapse into the "..."
            menu on scroll (building-view swap). */}
          <div className="listing-page__topbar-actions">
            <div className="listing-page__topbar-pair">
              <button
                type="button"
                className="listing-page__icon-btn"
                aria-label={t('listingCard.share', 'Share')}
                onClick={handleShare}
              >
                <img
                  className="listing-page__icon"
                  src={shareIcon}
                  alt=""
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="listing-page__icon-btn listing-page__icon-btn--close"
                aria-label={t('common.close')}
                onClick={handleClose}
              >
                <CloseIcon size={18} color="#29363d" />
              </button>
            </div>
            <div className="listing-page__topbar-more">
              <button
                type="button"
                className="listing-page__icon-btn"
                aria-label={t('building.controls.more', 'More')}
                aria-haspopup="menu"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <img
                  className="listing-page__icon"
                  src={overflowIcon}
                  alt=""
                  aria-hidden="true"
                />
              </button>
              {menuOpen && (
                <div
                  className="listing-page__menu-backdrop"
                  aria-hidden="true"
                  onClick={() => setMenuOpen(false)}
                />
              )}
              {menuOpen && (
                <div className="listing-page__menu" role="menu">
                  <button
                    type="button"
                    className="listing-page__menu-item"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      handleShare();
                    }}
                  >
                    <img
                      className="listing-page__menu-icon"
                      src={shareIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    {t('listingCard.share', 'Share')}
                  </button>
                  <button
                    type="button"
                    className="listing-page__menu-item"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      handleClose();
                    }}
                  >
                    <CloseIcon size={18} color="#29363d" />
                    {t('common.close')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="listing-page__body">
          {/* Facts first: stats (big h1) / price / chips lead the page. */}
          {hasStats && renderMainFacts()}
          {/* Marks the address's in-flow position so scroll progress can be read
            even though the address itself is sticky. Address + caption are
            direct body children so the address sticks across the whole scroll. */}
          <div
            className="listing-page__sentinel"
            ref={sentinelRef}
            aria-hidden="true"
          />
          {hasStats ? (
            <h2
              className="listing-page__address listing-page__address--compact"
              ref={addressRef}
            >
              {formatStreetLine(listing.address)}
            </h2>
          ) : (
            <h1 className="listing-page__address" ref={addressRef}>
              {formatStreetLine(listing.address)}
            </h1>
          )}
          <p className="listing-page__caption">{districtLine}</p>
          {/* No stats → the address keeps the big-h1 slot; price/chips follow. */}
          {!hasStats && renderMainFacts()}

          <div className="listing-page__hero">
            {renderGallery()}
            {renderDescription()}
            {renderPrompt()}
          </div>

          {renderSections()}
        </div>
      </div>
      {renderDockedCta()}
    </div>
  );
};

export default ListingPage;
